import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DonationFundInfoFragment } from '@graphql/aliases';
import { useCatalogContext } from '../../state/Catalog';
import { OnClickForCashDialogFn } from './ProductGrid.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { deriveTelemetryPage, DeriveTelemetryPageArgs } from '../../utils/deriveTelemetryPage';
import { useStoredCuration } from '../../hooks/useStoredCuration';
import { CustomItemDataType, Maybe, ProductCatalogAttributeSelectionType, ProductCatalogSearchQueryOrderBy, ProductCatalogStorefrontStatus } from '@graphql/generated';
import { CASH_FUNDS_ALIASES, isValidCashFundAlias } from '@apps/registry/common/utils/departmentAliases';
import { useSearchTermQueryParam } from '../../utils/queryParams';
import { PagesForTelemetry } from '../../Catalog.telemetry.types';
import { CatalogProductFilters } from '../RefinementFacet/RefinementFacet.types';
import { useRefinementsPanelProvider } from '../RefinementsPanel/RefinementsPanel.provider';
import { CatalogProductListFilterAttribute, UniversalItem, isCatalogItem } from '../../Catalog.types';
import { useRefinementFacetList } from '../RefinementFacetList/RefinementFacetList.controller';
import { ActiveRefinement } from '../ActiveRefinementsBar/ActiveRefinementsBar';
import { filterSupportedAttributeList } from '../../utils/filterSupportedAttributeList';
import { sortFilters } from '../../utils/sortFilters';
import { useCashFundDialogContext } from '../CashFundDialogProvider';
import { useOnIntersectingChange } from '@shared/utils/hooks/useOnIntersectingChange';
import { useValuePropCards } from '../ProductTile/components/ValuePropCard/hooks/useValuePropCards';
import { useTranslation } from '@shared/core';
import { BUCKETED_CUSTOM_CODE } from '../RefinementFacet/components/RefinementFacetPriceBuckets/RefinementFacetPriceBuckets.controller';
import { filterConsolidatedPriceList } from '../../utils/filterConsolidatedPriceList';
import { getTitleFromBucketCode } from '../../utils/priceRange';
import { DonationFundItemType } from '../../utils/interpretItemType';

export interface DonationFundInfoFragmentWithUrl extends DonationFundInfoFragment {
  productUrl?: string;
}
interface ProductGridControllerProps {
  products: ReadonlyArray<UniversalItem>;
  categoryLocator: Maybe<string>;
  categoryType: 'curation' | 'category';
  showSkeleton?: boolean;
  filters: CatalogProductFilters;
  shouldShowValuePropCards?: boolean;
  isMobile?: boolean;
}

const CASH_FUNDS_CATEGORIES = CASH_FUNDS_ALIASES.reduce(
  (cashFundCategories, cashFundCategoryName) => ({
    ...cashFundCategories,
    [cashFundCategoryName]: CustomItemDataType.cash,
  }),
  {},
);

export const CATEGORY_TITLE_TO_FUND_TYPE = {
  ...CASH_FUNDS_CATEGORIES,
  Charity: CustomItemDataType.charity,
} as const;
export type CategoryTitleToFundTypeKey = keyof typeof CATEGORY_TITLE_TO_FUND_TYPE;

export const useProductGridFilters = ({ filters, showSkeleton }: Pick<ProductGridControllerProps, 'filters' | 'showSkeleton'>) => {
  const [shouldShowFilterToggle, setShouldShowFilterToggle] = useState(true);
  const [isProductGridIntersecting, setIsProductGridIntersecting] = useState(false);
  const { closePanel, clearAllSearchFilters } = useRefinementsPanelProvider();
  const { handleOnFilterSelect, removeFilterGroup } = useRefinementFacetList();
  const { t } = useTranslation('catalogRegistry');
  const refinementsPanel = t('refinementsPanel');

  const activeFilters = useMemo((): Array<ActiveRefinement> => {
    const filterList = filterSupportedAttributeList(filters);
    const consolidatedFilterList = filterConsolidatedPriceList(filterList);
    const sortedFilters = sortFilters(consolidatedFilterList);
    const refinedFilters = sortedFilters.reduce((attributesList, currentFilter) => {
      if (!currentFilter.attributes.length) return attributesList;

      /**
       * If it's a price range filter, add one only pill for the entire range
       */
      if (
        currentFilter.attributeGroup.filterSelectionType === ProductCatalogAttributeSelectionType.priceRange &&
        currentFilter.attributes.some((att: CatalogProductListFilterAttribute) => att.compositeCode === BUCKETED_CUSTOM_CODE && att.isActive)
      ) {
        const { minValueInMinorUnits, maxValueInMinorUnits } = currentFilter.selectedPriceRange || {};
        const minValue = minValueInMinorUnits ? (minValueInMinorUnits / 100).toString() : undefined;
        const maxValue = maxValueInMinorUnits ? (maxValueInMinorUnits / 100).toString() : undefined;
        if (minValue && maxValue) {
          const newAttribute: ActiveRefinement = {
            value: `${minValue}|${maxValue}`,
            label: refinementsPanel.priceRange({ minValue, maxValue }),
            onRemove: () => removeFilterGroup(ProductCatalogAttributeSelectionType.priceRange),
          };
          attributesList.push(newAttribute);
          return attributesList;
        }
        if (minValue) {
          const newAttribute: ActiveRefinement = {
            value: minValue,
            label: refinementsPanel.priceGraterThan({ minValue }),
            onRemove: () => removeFilterGroup(ProductCatalogAttributeSelectionType.priceRange),
          };
          attributesList.push(newAttribute);
          return attributesList;
        }
        if (maxValue) {
          const newAttribute: ActiveRefinement = {
            value: maxValue,
            label: refinementsPanel.priceLowerThan({ maxValue }),
            onRemove: () => removeFilterGroup(ProductCatalogAttributeSelectionType.priceRange),
          };
          attributesList.push(newAttribute);
          return attributesList;
        }
        return attributesList;
      }

      /**
       * If it's not a price range filter, add a pill for each selected attribute
       */
      const filteredAttributes = currentFilter.attributes.filter((item: CatalogProductListFilterAttribute) => item.isActive);
      const sortedAttributes = filteredAttributes.sort((a, b) => a.value.title.localeCompare(b.value.title));
      const mappedAttributes = sortedAttributes.map((item: CatalogProductListFilterAttribute) => {
        const isPriceBucket = currentFilter.attributeGroup.filterSelectionType === ProductCatalogAttributeSelectionType.priceRange;
        const label = isPriceBucket ? getTitleFromBucketCode(item.value.code, refinementsPanel, true) : `${currentFilter.attributeGroup.title}: ${item.value.title}`;
        return {
          value: item.value.code,
          label: label,
          onRemove: () => {
            if (item.compositeCode) {
              handleOnFilterSelect({
                selectionType: currentFilter.attributeGroup.filterSelectionType,
                filterArgs: { compositeCode: item.compositeCode, value: false },
              });
            }
          },
        };
      });
      attributesList.push(...mappedAttributes);
      return attributesList;
    }, [] as Array<ActiveRefinement>);

    return refinedFilters;
  }, [filters, handleOnFilterSelect, refinementsPanel, removeFilterGroup]);

  useEffect(() => {
    setShouldShowFilterToggle(!!filters.length);
    if (!filters.length && !showSkeleton) {
      closePanel();
    }
  }, [filters, closePanel, showSkeleton]);

  const triggerPointRef = useRef(null);

  const onIntersectingTriggerPointEl = useCallback(
    (isIntersecting: boolean) => {
      if (isIntersecting && triggerPointRef.current) {
        setIsProductGridIntersecting(true);
      } else {
        setIsProductGridIntersecting(false);
      }
    },
    [setIsProductGridIntersecting],
  );

  useOnIntersectingChange(triggerPointRef, onIntersectingTriggerPointEl);

  return {
    shouldShowFilterToggle,
    isProductGridIntersecting,
    triggerPointRef,
    activeFilters,
    clearAllSearchFilters,
  };
};

export const useProductGridSortBy = () => {
  const { handleOnSortBy } = useRefinementFacetList();

  const handleSortBy = useCallback(
    (sortBy: ProductCatalogSearchQueryOrderBy) => {
      handleOnSortBy(sortBy);
    },
    [handleOnSortBy],
  );

  return {
    handleSortBy,
  };
};

export const useProductGridController = ({ categoryLocator, isMobile, products, shouldShowValuePropCards }: ProductGridControllerProps) => {
  const { eventId, selectedCategory, onAddItemToRegistryWithoutEvent, goToCatalogItem, isProductInRegistry } = useCatalogContext();
  const { openPreviewAndEditCatalogCashItemDialog, openAddCatalogCashFundDialog, openCustomCashFundDialog } = useCashFundDialogContext();
  const searchTerm = useSearchTermQueryParam();

  const deriveTelemetryPageForProduct = useCallback(
    (args: DeriveTelemetryPageArgs): PagesForTelemetry => {
      return searchTerm ? 'searchResults' : deriveTelemetryPage(args);
    },
    [searchTerm],
  );
  const { getRelatedCuration } = useStoredCuration(null);

  const fundType = CATEGORY_TITLE_TO_FUND_TYPE[selectedCategory?.title as CategoryTitleToFundTypeKey];
  const showCustomCashTile = isValidCashFundAlias(selectedCategory?.title) || selectedCategory?.title === 'Charity';

  const shouldDisplayValuePropCards = !!shouldShowValuePropCards && products.length > 30;
  const { productGridItems } = useValuePropCards({
    shouldDisplayValuePropCards,
    categoryLocator,
    products,
    shouldShowCustomCashTile: showCustomCashTile,
  });

  const handleClickForCashDialog = useEventCallback<OnClickForCashDialogFn>(({ clickedProduct, isQuickAdd, itemPosition }) => {
    if (eventId) {
      if (isQuickAdd) {
        openAddCatalogCashFundDialog({ addMethodType: 'quick add', product: clickedProduct, itemPosition });
      } else {
        openPreviewAndEditCatalogCashItemDialog({ product: clickedProduct, itemPosition });
      }
    } else {
      const storedCuration = getRelatedCuration(clickedProduct);
      onAddItemToRegistryWithoutEvent?.({
        type: 'catalog',
        itemType: (clickedProduct.itemType || fundType) as DonationFundItemType,
        catalogLocator: categoryLocator,
        curation: storedCuration,
        category: selectedCategory,
        product: clickedProduct,
        isCreateFromCustom: false,
        itemPosition,
        telemetryArgs: {
          method: isQuickAdd ? 'quick add' : 'pdp cta',
          page: deriveTelemetryPageForProduct({ view: 'productGrid', product: clickedProduct, selectedCategory }),
        },
      });
    }
  });

  const handleOnCustomCashTileClick = useEventCallback(() => {
    if (eventId) {
      openCustomCashFundDialog({ addMethodType: 'custom', fundType });
    } else if (categoryLocator) {
      // Currently, you can only add a custom cash item when you're at the cash department catalog page
      onAddItemToRegistryWithoutEvent?.({
        catalogLocator: categoryLocator,
        type: 'catalog',
        itemType: fundType as DonationFundItemType,
        product: null,
        curation: null,
        category: selectedCategory,
        isCreateFromCustom: true,
        itemPosition: null,
        telemetryArgs: {
          method: 'custom',
          page: deriveTelemetryPageForProduct({ view: 'productGrid', product: null, selectedCategory }),
        },
      });
    }
  });

  // checks if at least one product of the list has 'comingSoon' storefront status
  const hasAnyStorefrontWarning = useMemo<boolean>(() => {
    return products.some(product => isCatalogItem(product) && product.storefrontInfo.storefrontStatus === ProductCatalogStorefrontStatus.comingSoon);
  }, [products]);

  return {
    eventId,
    showCustomCashTile,
    deriveTelemetryPageForProduct,
    handleClickForCashDialog,
    handleOnCustomCashTileClick,
    fundType: isValidCashFundAlias(selectedCategory?.title) ? 'Cash Fund' : 'Charity Fund',
    hasAnyStorefrontWarning,
    selectedCategory,
    productGridItems,
    goToCatalogItem,
    isProductInRegistry,
  };
};
